// components/contacts/ViewContacts.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import contactRequests from "../../api/Customer/ContactRequests";
import {
  Search,
  Edit,
  Trash2,
  Save,
  X,
  Grid,
  List,
  User,
  Phone,
  Mail,
  Calendar,
  Cake,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

// Today's date as "YYYY-MM-DD", used to cap birthday date inputs so a future date can't be picked.
const TODAY_ISO_DATE = new Date().toISOString().slice(0, 10);

// Native <input type="date"> requires exactly "YYYY-MM-DD". The API returns
// birthday as a full ISO datetime ("2026-08-19T00:00:00.000000Z"), which the
// input would silently fail to populate - slicing to the date portion first
// keeps this working for either shape.
const toDateInputValue = (birthdayString) => (birthdayString ? birthdayString.slice(0, 10) : "");

// Minimal DND toggle switch
const DndToggle = React.memo(({ dndStatus, onToggle, isToggling }) => {
  return (
    <button
      onClick={onToggle}
      disabled={isToggling}
      type="button"
      aria-pressed={dndStatus}
      className="inline-flex items-center gap-1.5 group disabled:opacity-60"
    >
      <span
        className={`relative inline-flex h-4 w-7 flex-shrink-0 rounded-full transition-colors duration-200 ${
          dndStatus ? "bg-red-500" : "bg-green-500"
        }`}
      >
        {isToggling ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-2.5 h-2.5 rounded-full border-2 border-white animate-spin border-t-transparent" />
          </span>
        ) : (
          <span
            className={`inline-block h-3 w-3 mt-0.5 rounded-full bg-white shadow transition-transform duration-200 ${
              dndStatus ? "translate-x-3.5" : "translate-x-0.5"
            }`}
          />
        )}
      </span>
      <span className={`text-xs font-medium ${dndStatus ? "text-red-600" : "text-green-600"}`}>
        {dndStatus ? "Excluded" : "Included"}
      </span>
    </button>
  );
});
DndToggle.displayName = "DndToggle";

// Memoized EditRow component for table view
const EditRow = React.memo(({ contact, onSave, onCancel, onToggleDnd, isTogglingDnd }) => {
  const [localEditForm, setLocalEditForm] = useState({
    first_name: contact.first_name || "",
    last_name: contact.last_name || "",
    email: contact.email || "",
    phone_number: contact.phone_number || "",
    birthday: toDateInputValue(contact.birthday),
  });

  const handleSave = useCallback(() => {
    onSave(contact.id, localEditForm);
  }, [contact.id, localEditForm, onSave]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleChange = useCallback((field) => (e) => {
    setLocalEditForm(prev => ({ ...prev, [field]: e.target.value }));
  }, []);

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col space-y-2">
          <input
            type="text"
            value={localEditForm.first_name}
            onChange={handleChange('first_name')}
            className="px-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="First Name"
          />
          <input
            type="text"
            value={localEditForm.last_name}
            onChange={handleChange('last_name')}
            className="px-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Last Name"
          />
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <input
          type="text"
          value={localEditForm.phone_number}
          onChange={handleChange('phone_number')}
          className="px-3 py-1 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Phone Number"
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <input
          type="email"
          value={localEditForm.email}
          onChange={handleChange('email')}
          className="px-3 py-1 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Email"
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <input
          type="date"
          value={localEditForm.birthday}
          onChange={handleChange('birthday')}
          max={TODAY_ISO_DATE}
          className="px-3 py-1 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(contact.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <DndToggle
          dndStatus={contact.dnd_status}
          onToggle={() => onToggleDnd(contact)}
          isToggling={isTogglingDnd}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-2">
          <button
            onClick={handleSave}
            className="px-3 py-1 text-xs text-white bg-green-600 rounded hover:bg-green-700 transition-colors"
          >
            <Save className="w-4 h-4" />
          </button>
          <button
            onClick={handleCancel}
            className="px-3 py-1 text-xs text-white bg-gray-600 rounded hover:bg-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
});

EditRow.displayName = 'EditRow';

// Memoized ContactRow component for table view
const ContactRow = React.memo(({ contact, onEdit, onDelete, onToggleDnd, isDeleting, isTogglingDnd, formatDate, formatBirthday }) => {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="h-5 w-5 text-blue-600" />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {contact.first_name || "Unknown"} {contact.last_name || ""}
            </div>
            <div className="text-sm text-gray-500">
              ID: {contact.id || "N/A"}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center text-sm text-gray-900">
          <Phone className="mr-2 h-4 w-4 text-gray-400" />
          {contact.phone_number || "N/A"}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center text-sm text-gray-900">
          <Mail className="mr-2 h-4 w-4 text-gray-400" />
          {contact.email || "N/A"}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center text-sm text-gray-900">
          <Cake className="mr-2 h-4 w-4 text-gray-400" />
          {formatBirthday(contact.birthday)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <Calendar className="mr-2 h-4 w-4 text-gray-400" />
          {formatDate(contact.created_at)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <DndToggle
          dndStatus={contact.dnd_status}
          onToggle={() => onToggleDnd(contact)}
          isToggling={isTogglingDnd}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(contact)}
            className="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(contact.id)}
            disabled={isDeleting}
            className="px-3 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {isDeleting ? (
              <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </td>
    </tr>
  );
});

ContactRow.displayName = 'ContactRow';

// Memoized CardEdit component
const CardEdit = React.memo(({ contact, onSave, onCancel }) => {
  const [localEditForm, setLocalEditForm] = useState({
    first_name: contact.first_name || "",
    last_name: contact.last_name || "",
    email: contact.email || "",
    phone_number: contact.phone_number || "",
    birthday: toDateInputValue(contact.birthday),
  });

  const handleSave = useCallback(() => {
    onSave(contact.id, localEditForm);
  }, [contact.id, localEditForm, onSave]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleChange = useCallback((field) => (e) => {
    setLocalEditForm(prev => ({ ...prev, [field]: e.target.value }));
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={localEditForm.first_name}
            onChange={handleChange('first_name')}
            className="px-3 py-1 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="First Name"
          />
          <input
            type="text"
            value={localEditForm.last_name}
            onChange={handleChange('last_name')}
            className="px-3 py-1 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Last Name"
          />
        </div>
      </div>
      <input
        type="text"
        value={localEditForm.phone_number}
        onChange={handleChange('phone_number')}
        className="px-3 py-1 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Phone Number"
      />
      <input
        type="email"
        value={localEditForm.email}
        onChange={handleChange('email')}
        className="px-3 py-1 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Email"
      />
      <div>
        <label className="block mb-1 text-xs text-gray-500">Birthday</label>
        <input
          type="date"
          value={localEditForm.birthday}
          onChange={handleChange('birthday')}
          max={TODAY_ISO_DATE}
          className="px-3 py-1 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="flex space-x-2 pt-2">
        <button
          onClick={handleSave}
          className="flex-1 px-3 py-2 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors"
        >
          Save
        </button>
        <button
          onClick={handleCancel}
          className="flex-1 px-3 py-2 text-xs font-medium text-white bg-gray-600 rounded hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
});

CardEdit.displayName = 'CardEdit';

// Memoized ContactCard component
const ContactCard = React.memo(({ contact, onEdit, onDelete, onToggleDnd, isDeleting, isTogglingDnd, formatDate, formatBirthday }) => {
  return (
    <>
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="h-5 w-5 text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-gray-900">
              {contact.first_name || "Unknown"} {contact.last_name || ""}
            </h3>
            <p className="text-xs text-gray-500">ID: {contact.id || "N/A"}</p>
          </div>
        </div>
        <div className="flex space-x-1">
          <button
            onClick={() => onEdit(contact)}
            className="p-1 text-blue-600 hover:text-blue-800"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(contact.id)}
            disabled={isDeleting}
            className="p-1 text-red-600 hover:text-red-800"
          >
            {isDeleting ? (
              <div className="w-4 h-4 rounded-full border-2 border-red-600 animate-spin border-t-transparent"></div>
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center text-sm text-gray-600">
          <Phone className="mr-2 h-4 w-4 text-gray-400" />
          {contact.phone_number || "N/A"}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Mail className="mr-2 h-4 w-4 text-gray-400" />
          {contact.email || "No email"}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Cake className="mr-2 h-4 w-4 text-gray-400" />
          {formatBirthday(contact.birthday)}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Calendar className="mr-2 h-4 w-4 text-gray-400" />
          {formatDate(contact.created_at)}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="text-gray-500">DND:</span>
          <DndToggle
            dndStatus={contact.dnd_status}
            onToggle={() => onToggleDnd(contact)}
            isToggling={isTogglingDnd}
          />
        </div>
      </div>
    </>
  );
});

ContactCard.displayName = 'ContactCard';

const ViewContacts = ({ groupSlug, groupName, onRefresh }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'card'
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  
  // Edit state - now only stores the ID being edited
  const [editingId, setEditingId] = useState(null);
  
  // Delete state
  const [deletingId, setDeletingId] = useState(null);

  // DND toggle state
  const [togglingDndId, setTogglingDndId] = useState(null);

  // Sort state
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' or 'desc'

  // Format date function
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  }, []);

  // Format a birthday for display. The API returns this as a full ISO
  // datetime ("2026-08-19T00:00:00.000000Z"), not a plain "YYYY-MM-DD"
  // string, so slice to the date portion first — this also still works fine
  // if a plain date string is ever passed in instead.
  // Deliberately does NOT do `new Date(birthdayString)` directly: that parses the
  // string as UTC midnight, which can render a day early in negative-UTC-offset
  // timezones. Building the Date from its year/month/day parts avoids that.
  const formatBirthday = useCallback((birthdayString) => {
    if (!birthdayString) return "—";
    const [birthdayYear, birthdayMonth, birthdayDay] = birthdayString.slice(0, 10).split("-").map(Number);
    if (!birthdayYear || !birthdayMonth || !birthdayDay) return "—";
    try {
      const birthdayDate = new Date(birthdayYear, birthdayMonth - 1, birthdayDay);
      return birthdayDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load contacts
  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contactRequests.getGroupContacts(
        groupSlug,
        pagination.currentPage,
        pagination.perPage,
        debouncedSearchTerm
      );
      
      if (response.success && response.data?.data?.status_code === "000") {
        // Extract contacts from the nested structure: data.data.data.data
        const contactsData = response.data.data.data?.data || [];
        const paginationData = response.data.data.data?.pagination || {};

        // Ensure contacts are sorted by created_at based on sortOrder
        const contactsArray = Array.isArray(contactsData) ? contactsData : [];
        const sortedContacts = contactsArray.slice().sort((a, b) => {
          const dateA = a?.created_at ? new Date(a.created_at) : new Date(0);
          const dateB = b?.created_at ? new Date(b.created_at) : new Date(0);
          return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
        });

        setContacts(sortedContacts);
        setPagination({
          currentPage: paginationData.current_page || pagination.currentPage,
          perPage: paginationData.per_page || pagination.perPage,
          total: paginationData.total || 0,
          lastPage: paginationData.last_page || 1,
          from: paginationData.from || 0,
          to: paginationData.to || 0,
        });
      } else {
        toast.error("Failed to load contacts");
        setContacts([]);
      }
    } catch (error) {
      console.error("Error loading contacts:", error);
      toast.error("Error loading contacts");
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [groupSlug, pagination.currentPage, pagination.perPage, debouncedSearchTerm, sortOrder]);

  // Load contacts on mount and when dependencies change
  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  }, []);

  // Handle per page change
  const handlePerPageChange = useCallback((newPerPage) => {
    setPagination(prev => ({ ...prev, perPage: newPerPage, currentPage: 1 }));
  }, []);

  // Start editing a contact
  const handleEditClick = useCallback((contact) => {
    setEditingId(contact.id);
  }, []);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  // Save edited contact
  const handleSaveEdit = useCallback(async (contactId, formData) => {
    try {
      const payload = {
        contact_group_slug: groupSlug,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: formData.phone_number,
        birthday: formData.birthday || null,
      };

      const response = await contactRequests.editContact(contactId, payload);
      
      if (response.success) {
        toast.success("Contact updated successfully!");
        setEditingId(null);
        await loadContacts();
        if (onRefresh) onRefresh(); // Refresh group stats
      } else {
        toast.error(response.error?.message || "Failed to update contact");
      }
    } catch (error) {
      toast.error("Error updating contact");
      console.error("Edit error:", error);
    }
  }, [groupSlug, loadContacts, onRefresh]);

  // Toggle DND status
  const handleToggleDnd = useCallback(async (contact) => {
    const newDndStatus = !contact.dnd_status;
    try {
      setTogglingDndId(contact.id);
      const response = await contactRequests.toggleContactDnd(
        contact.id,
        newDndStatus,
        groupSlug
      );
      if (response.success) {
        setContacts(prev =>
          prev.map(c => c.id === contact.id ? { ...c, dnd_status: newDndStatus } : c)
        );
        toast.success(`DND ${newDndStatus ? "enabled" : "disabled"} for contact`);
      } else {
        toast.error(response.error?.message || "Failed to update DND status");
      }
    } catch (error) {
      toast.error("Error updating DND status");
    } finally {
      setTogglingDndId(null);
    }
  }, [groupSlug]);

  // Handle delete contact
  const handleDeleteClick = useCallback(async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) {
      return;
    }

    try {
      setDeletingId(contactId);
      const response = await contactRequests.deleteContact(contactId);
      
      if (response.success) {
        toast.success("Contact deleted successfully!");
        await loadContacts();
        if (onRefresh) onRefresh(); // Refresh group stats
      } else {
        toast.error(response.error?.message || "Failed to delete contact");
      }
    } catch (error) {
      toast.error("Error deleting contact");
      console.error("Delete error:", error);
    } finally {
      setDeletingId(null);
    }
  }, [loadContacts, onRefresh]);

  // Table view component
  const TableView = useMemo(() => {
    if (!Array.isArray(contacts)) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
          <div className="text-red-500">Error: Contacts data is not an array</div>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-sm">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Birthday
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center gap-2 cursor-pointer hover:text-gray-700 transition-colors" onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}>
                  <span>Created</span>
                  {sortOrder === 'desc' ? (
                    <ArrowDown className="w-4 h-4" />
                  ) : (
                    <ArrowUp className="w-4 h-4" />
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                DND Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contacts.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  No contacts found
                </td>
              </tr>
            ) : (
              contacts.map((contact) => (
                editingId === contact.id ? (
                  <EditRow
                    key={contact.id}
                    contact={contact}
                    onSave={handleSaveEdit}
                    onCancel={handleCancelEdit}
                    onToggleDnd={handleToggleDnd}
                    isTogglingDnd={togglingDndId === contact.id}
                  />
                ) : (
                  <ContactRow
                    key={contact.id}
                    contact={contact}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                    onToggleDnd={handleToggleDnd}
                    isDeleting={deletingId === contact.id}
                    isTogglingDnd={togglingDndId === contact.id}
                    formatDate={formatDate}
                    formatBirthday={formatBirthday}
                  />
                )
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  }, [contacts, editingId, sortOrder, deletingId, togglingDndId, handleSaveEdit, handleCancelEdit, handleEditClick, handleDeleteClick, handleToggleDnd, formatDate, formatBirthday]);

  // Card view component
  const CardView = useMemo(() => {
    if (!Array.isArray(contacts)) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
          <div className="text-red-500">Error: Contacts data is not an array</div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {contacts.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            No contacts found
          </div>
        ) : (
          contacts.map((contact) => (
            <div key={contact.id} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow">
              {editingId === contact.id ? (
                <CardEdit
                  contact={contact}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                />
              ) : (
                <ContactCard
                  contact={contact}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  onToggleDnd={handleToggleDnd}
                  isDeleting={deletingId === contact.id}
                  isTogglingDnd={togglingDndId === contact.id}
                  formatDate={formatDate}
                  formatBirthday={formatBirthday}
                />
              )}
            </div>
          ))
        )}
      </div>
    );
  }, [contacts, editingId, deletingId, togglingDndId, handleSaveEdit, handleCancelEdit, handleEditClick, handleDeleteClick, handleToggleDnd, formatDate, formatBirthday]);

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Contacts in {groupName}
            </h2>
            <p className="text-sm text-gray-600">
              {pagination.total} contact{pagination.total !== 1 ? "s" : ""} found
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-64 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* View toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-2 ${viewMode === "table" ? "bg-blue-600 text-white" : "bg-white text-gray-700"}`}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("card")}
                className={`px-3 py-2 ${viewMode === "card" ? "bg-blue-600 text-white" : "bg-white text-gray-700"}`}
              >
                <Grid className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
            <span className="text-gray-600">Loading contacts...</span>
          </div>
        </div>
      ) : !Array.isArray(contacts) ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
          <div className="text-red-500 mb-2">Error: Contacts data is not in expected format</div>
          <button
            onClick={() => {
              //console.log("Current contacts state:", contacts);
              //console.log("Current pagination:", pagination);
              loadContacts();
            }}
            className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            Debug & Retry
          </button>
        </div>
      ) : contacts.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
            <User className="text-2xl text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No Contacts Found
          </h3>
          <p className="text-gray-500 mb-4">
            {debouncedSearchTerm
              ? `No contacts match "${debouncedSearchTerm}". Try a different search.`
              : "This group has no contacts yet."}
          </p>
          {debouncedSearchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              Clear Search
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Contacts view */}
          {viewMode === "table" ? TableView : CardView}
          
          {/* Pagination */}
          {pagination.total > pagination.perPage && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-600">
                  Showing {pagination.from} to {pagination.to} of {pagination.total} contacts
                </div>
                
                <div className="flex items-center space-x-3">
                  {/* Per page selector */}
                  <select
                    value={pagination.perPage}
                    onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                  </select>
                  
                  {/* Page navigation */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className={`p-2 rounded-lg ${pagination.currentPage === 1 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(pagination.lastPage, 5) }, (_, i) => {
                        let pageNum;
                        if (pagination.lastPage <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.lastPage - 2) {
                          pageNum = pagination.lastPage - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-1 text-sm rounded-lg ${pagination.currentPage === pageNum ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.lastPage}
                      className={`p-2 rounded-lg ${pagination.currentPage === pagination.lastPage ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ViewContacts;